import { useEffect } from "react";
import {
  type QueryParamConfig,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import type { CreatedTemplate, TemplateType } from "common/types";

import {
  TEMPLATE_SORTING_ORDERS,
  TEMPLATE_TYPE_SORTING_ORDERS,
} from "../constants";

export default function useTemplatesFilters(templates: CreatedTemplate[]) {
  const [sortOrder, setSortOrder] = useQueryParam(
    "sortOrder",
    withDefault(
      StringParam as QueryParamConfig<keyof typeof TEMPLATE_SORTING_ORDERS>,
      "latest",
    ),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );
  const [searchText, setSearchText] = useQueryParam(
    "search",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );
  const [templateType, setTemplateType] = useQueryParam(
    "type",
    withDefault(StringParam as QueryParamConfig<TemplateType>, "NOTE_TEMPLATE"),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  useEffect(() => {
    //template sorting orders are different depending on the template type.
    //when the template type changes, if the sorting order is invalid it's automatically changed to a valid sorting order
    if (!TEMPLATE_TYPE_SORTING_ORDERS[templateType].includes(sortOrder)) {
      setSortOrder(TEMPLATE_TYPE_SORTING_ORDERS[templateType][0]);
    }
  }, [templateType]);

  const filteredTemplates = templates
    ? templates
        .filter(
          ({ name, type }) =>
            name
              .trim()
              .toLowerCase()
              .includes(searchText.trim().toLowerCase()) &&
            (templateType ? type === templateType : true),
        )
        .sort(TEMPLATE_SORTING_ORDERS[sortOrder].sortFn)
    : [];

  function clearFilters() {
    setSearchText("");
    setTemplateType(null);
  }

  return {
    searchText,
    setSearchText,
    templateType,
    setTemplateType,
    sortOrder,
    setSortOrder,
    filteredTemplates,
    hasFilters: !!searchText || !!templateType,
    clearFilters,
  };
}
